import React, {ComponentProps, useMemo} from 'react'
import {defineMessages} from 'react-intl'
import {useSelector} from 'react-redux'
import {useShouldOfferLeadToRooms} from 'hooks/useShouldOfferLeadToRooms'
import {getOfferAccessFlowSettings} from 'modules/sapiSearch/selectors'
import {SearchOffer} from 'modules/sapiSearch/slice'

import {Button, Props as ButtonProps} from '@vio/atlas/Button'
import {Box} from '@vio/atlas/helpers/Box'
import {Lock} from '@vio/atlas/helpers/Lock'
import {Icon} from '@vio/atlas/Icon'
import {getIsPrivateDeal} from '@vio/core/src/auth/modules/selectors'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import getIsBofhOffer from '@vio/core/src/offer/business/isBofhOffer'

interface BaseCTAProps {
  /** The hotel id */
  hotelId: string
  /** The offer object */
  offer: SearchOffer
  /** Whether the button full width of parent */
  fullWidth?: boolean
  /** Whether the locked icon should be shown */
  showLockIcon?: boolean
  /** Whether the button is in the loading state */
  loading?: ButtonProps['loading']
  /** Whether the offer should be locked to web users */
  isAppLocked?: boolean
  /** Whether the button should use the secondary variant */
  withSecondaryVariant?: boolean
  /** Whether the button is disabled */
  disabled?: boolean
}

interface SplitBookingCTAProps {
  /** Whether the button full width of parent */
  fullWidth?: boolean
  /** Whether the locked icon should be shown */
  showLockIcon?: boolean
  /** Whether the button is in the loading state */
  loading?: ButtonProps['loading']
  /** Whether the offer should be locked to web users */
  isAppLocked?: boolean
  /** Whether the button is disabled */
  disabled?: boolean
}

const ctaMessages = defineMessages({
  unlock: {
    id: 'signUpCTAUnlock',
    defaultMessage: 'Unlock'
  },
  viewDeal: {
    id: 'viewDeal',
    defaultMessage: 'View deal'
  },
  static: {
    id: 'checkAvailability',
    defaultMessage: 'Check availability'
  },
  viewRooms: {
    id: 'viewRooms',
    defaultMessage: 'View rooms'
  },
  appLockedExclusiveV2: {
    id: 'offer.appExclusiveCta',
    defaultMessage: 'View on app'
  }
})

const getIcon = ({
  shouldShowLock,
  isLocked,
  isAppLocked
}: {
  shouldShowLock: boolean
  isAppLocked: boolean
  isLocked: boolean
}) => {
  if (isAppLocked) {
    return <Icon name="Iphone" />
  }
  if (shouldShowLock) {
    return <Lock size={20} isLocked={isLocked} />
  }
}

const SplitBookingCTA = ({
  loading = false,
  fullWidth = false,
  showLockIcon = false,
  isAppLocked = false,
  disabled = false
}: SplitBookingCTAProps) => {
  const shouldGoToRooms = useShouldOfferLeadToRooms()

  const ctaMessage = () => {
    if (isAppLocked) return ctaMessages.appLockedExclusiveV2
    if (shouldGoToRooms) return ctaMessages.viewRooms

    return ctaMessages.viewDeal
  }
  const showChevronIcon = !isAppLocked && !shouldGoToRooms

  const CtaProps: ComponentProps<typeof Button> = {
    ...(isAppLocked
      ? {
          dataId: 'PrivateDealCTA',
          variant: 'secondary',
          showIcon: showChevronIcon
        }
      : {
          dataId: 'OfferCTA',
          showIcon: showChevronIcon,
          variant: 'primary'
        })
  }

  return (
    <Button
      {...CtaProps}
      size="lg"
      fullWidth={fullWidth}
      loading={loading}
      disabled={disabled}
      iconStart={
        <Box display="flex">
          {getIcon({
            shouldShowLock: showLockIcon,
            isAppLocked,
            isLocked: false
          })}
        </Box>
      }
      iconEnd={
        showChevronIcon && (
          <Box hasMirror>
            <Icon name="ChevronRight" />
          </Box>
        )
      }
    >
      <FormattedMessageWrapper {...ctaMessage()} />
    </Button>
  )
}

const BaseCTA = ({
  offer,
  hotelId,
  fullWidth = false,
  showLockIcon = false,
  loading,
  isAppLocked = false,
  withSecondaryVariant = false,
  disabled = false
}: BaseCTAProps) => {
  const isPrivate = useSelector(getIsPrivateDeal(offer))
  const isBofhOffer = getIsBofhOffer(offer)

  const shouldGoToRooms = useShouldOfferLeadToRooms(isBofhOffer)
  const {isAuthLocked: isLocked, isSpecial} = useSelector(state =>
    getOfferAccessFlowSettings(state, hotelId, offer?.id)
  )

  const shouldShowViewRooms = shouldGoToRooms && !isSpecial
  const showChevronIcon =
    !(isPrivate && isLocked) && !isAppLocked && !shouldShowViewRooms

  const ctaMessage = () => {
    if (isAppLocked) return ctaMessages.appLockedExclusiveV2
    if (isLocked) return ctaMessages.unlock
    if (shouldShowViewRooms) return ctaMessages.viewRooms
    return ctaMessages.viewDeal
  }

  const CtaProps: ComponentProps<typeof Button> = useMemo(() => {
    // applies the secondary variant to the offer CTA when it is displayed alongside special CTAs
    // this includes scenarios where it is alongside the 'Deal Freeze' button or the 'Web2App' promotion
    if (withSecondaryVariant) {
      return {
        dataId: 'OfferCTA',
        variant: 'primary'
      }
    }

    if (isPrivate || isAppLocked) {
      return {
        dataId: 'PrivateDealCTA',
        variant: 'primary'
      }
    }

    return {
      dataId: 'OfferCTA',
      variant: 'primary'
    }
  }, [withSecondaryVariant, isAppLocked, isPrivate])

  const shouldShowLock = showLockIcon && isPrivate

  return (
    <Button
      {...CtaProps}
      size="lg"
      fullWidth={fullWidth}
      loading={loading}
      disabled={disabled}
      iconStart={
        <Box display="flex">
          {getIcon({shouldShowLock, isAppLocked, isLocked})}
        </Box>
      }
      iconEnd={
        showChevronIcon && (
          <Box hasMirror>
            <Icon name="ChevronRight" />
          </Box>
        )
      }
    >
      <FormattedMessageWrapper {...ctaMessage()} />
    </Button>
  )
}

type Props = (BaseCTAProps | SplitBookingCTAProps) & {
  isSplitBooking?: boolean
}

export const OfferCTA = (props: Props) => {
  if (props.isSplitBooking) {
    return <SplitBookingCTA {...props} />
  }

  return <BaseCTA {...(props as BaseCTAProps)} />
}
